import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const LegalView: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { getCopy } = useCopy();
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasAcceptedMembership, setHasAcceptedMembership] = useState(false);
  const [showItemsModal, setShowItemsModal] = useState(false);

  const handleBack = () => {
    navigate("/summary");
  };

  const handleContinue = () => {
    if (hasAcceptedTerms && hasAcceptedMembership) {
      console.log("User accepted legal terms, checking product review setting");
      // Check if product review and scan is enabled
      if (state.settings.productReviewAndScan) {
        console.log("Product review and scan enabled, proceeding to rate returns screen");
        navigate("/rate-returns");
      } else {
        console.log("Product review and scan disabled, skipping to offer screen");
        navigate("/offer");
      }
    }
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  const isValid = hasAcceptedTerms && hasAcceptedMembership;

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="141:4420"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-10">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Main heading */}
          <div className="flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading 
                  title={getCopy('legal.heading')}
                  subtitle={getCopy('legal.subheading')}
                />
              </motion.div>
            </div>
          </div>

          {/* Rows 2 & 3 - Terms and Conditions Section + Checkboxes spanning two rows */}
          <div className="row-span-2 flex flex-col gap-8 justify-end">
            <motion.div 
              className="flex flex-col gap-6 w-full max-w-[920px] mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {/* Terms header */}
              <div className="flex flex-row items-start justify-between px-8 py-4">
                <h3 className="font-brand-primary font-medium text-[24px] leading-[32px] tracking-[-0.24px] text-brand-text-primary">
                  {getCopy('legal.termsHeading')}
                </h3>
              </div>

              {/* Scrollable terms content */}
              <div className="bg-brand-surface rounded-3xl p-6 h-[320px] overflow-y-auto">
                <div className="font-brand-primary font-light text-[24px] leading-[31px] text-brand-text-primary">
                  <MultiLineCopy 
                    text={getCopy('legal.termsText')}
                    lineClassName="mb-6"
                  />
                </div>
              </div>
            </motion.div>

            {/* Checkboxes Section */}
            <motion.div 
              className="flex flex-col gap-4 w-full max-w-[920px] mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {/* Terms checkbox */}
              <div className="flex flex-row gap-6 items-center py-4">
                <button
                  onClick={() => setHasAcceptedTerms(!hasAcceptedTerms)}
                  className={`w-10 h-10 rounded-xl border-2 border-brand-text-primary flex items-center justify-center transition-colors duration-200 bg-brand-background
                  }`}
                >
                  {hasAcceptedTerms && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--brand-text-primary)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  )}
                </button>
                <p 
                  className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary cursor-pointer select-none"
                  onClick={() => setHasAcceptedTerms(!hasAcceptedTerms)}
                >
                  {getCopy('legal.termsCheckbox')}
                </p>
              </div>

              {/* Membership checkbox */}
              <div className="flex flex-row gap-6 items-center py-4">
                <button
                  onClick={() => setHasAcceptedMembership(!hasAcceptedMembership)}
                  className={`w-10 h-10 rounded-xl border-2 border-brand-text-primary flex items-center justify-center transition-colors duration-200 bg-brand-background
                  }`}
                >
                  {hasAcceptedMembership && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--brand-text-primary)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  )}
                </button>
                <p 
                  className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary cursor-pointer select-none"
                  onClick={() => setHasAcceptedMembership(!hasAcceptedMembership)}
                >
                  {getCopy('legal.membershipCheckbox')}
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - Back/Continue buttons and footer */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex flex-row gap-10 w-full">
            <Button variant="secondary" onClick={handleBack}>
              {getCopy('legal.buttons.secondary')}
            </Button>
            <Button 
              variant={isValid ? "primary" : "primary"} 
              onClick={handleContinue}
              disabled={!isValid}
              className={!isValid ? "opacity-20" : ""}
            >
              {getCopy('legal.buttons.primary')}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default LegalView;
